import ReactModal from 'react-modal';
import General from '../utils/GeneralUtils';
import './TitleFieldsSlideout.scss';

interface TitleFieldsSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
}

// Title field values - sourced from @edulead/utils TitleConfig.ts (all lowercase)
const TITLE_MODIFIERS = [
  'assistant',
  'associate',
  'administrative',
  'chief',
  'executive',
  'deputy',
  'interim',
  'former',
  'vice',
  'acting',
  'temporary',
  'senior',
  'junior',
  'substitute',
  'lead',
  'head',
];

const TITLE_ROLES = [
  'superintendent',
  'director',
  'coordinator',
  'manager',
  'officer',
  'specialist',
  'supervisor',
  'principal',
  'counselor',
  'dean',
  'analyst',
  'administrator',
  'program specialist',
  'hr generalist',
  'assistant',
  'professor',
  'teacher',
  'paraprofessional',
  'member',
  'custodian',
  'coach',
  'instructor',
  'president',
  'bus driver',
  'nurse',
  'librarian',
  'tutor',
  'paraeducator',
  'trainer',
  'aide',
  'secretary',
  'psychologist',
  'staff',
  'chair',
  'registrar',
  'therapist',
  'treasurer',
  'cook',
  'social worker',
  'pathologist',
  'bookkeeper',
  'faculty',
  'developer',
  'trustee',
  'clerk',
  'interventionist',
  'receptionist',
  'student',
  'advisor',
  'parent',
  'diagnostician',
  'cleaner',
  'pastor',
  'crossing guard',
  'accountant',
  'sponsor',
  'technician',
  'mechanic',
  'educator',
  'consultant',
  'author',
  'groundskeeper',
  'moderator',
  'interpreter',
  'data processor',
  'facilitator',
  'engineer',
  'recruiter',
];

const TITLE_AREAS = [
  'special education',
  'technology',
  'human resources',
  'student services',
  'operations',
  'curriculum and instruction',
  'finance',
  'facilities',
  'transportation',
  'admissions',
  'athletics',
  'development',
  'communications',
  'special services',
  'food services',
  'teaching and learning',
  'advancement',
  'education',
  'maintenance',
  'purchasing',
  'accounting',
  'security',
  'enrollment',
  'counseling',
  'health services',
  'federal programs',
  'marketing',
  'support services',
  'music',
  'student support',
  'public information',
  'elementary education',
  'secondary education',
  'schools',
  'institutional advancement',
  'alumni',
  'board',
  'cafeteria',
  'diversity equity inclusion',
  'band',
  'campus',
  'academics',
  'aquatics',
  'special projects',
  'elementary school',
  'middle school',
  'high school',
  'secondary school',
  'kindergarten',
  'department',
  'science',
  'social studies',
  'speech',
  'mathematics',
  'spanish',
  'language arts',
  'custodial',
  'history',
  'library',
  'preschool',
  'business',
  'attendance',
  'reading',
  'physical therapy',
  'english',
  'biology',
  'choir',
  'french',
  'chemistry',
  'administration',
  'title 1',
  'educational support',
  'art',
  'nursing',
  'stem',
  'cte',
  'compliance',
  'mental health',
  'exceptional children',
  'wellness',
  'mtss',
  'behavioral health',
  'autism',
  'social emotional learning',
  'english language learner',
];

export function TitleFieldsSlideout({ isOpen, onClose }: TitleFieldsSlideoutProps) {
  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onRequestClose={onClose}
      style={General.getSliderDefaults({ width: 'min(90vw, 550px)' })}
      parentSelector={General.getModalRoot}
    >
      <div className="title-fields-slideout">
        <div className="header">
          <h3>Title Field Values</h3>
        </div>

        <div className="section intro">
          <p>
            Contact titles are parsed into structured components: <strong>Title Modifier</strong>, <strong>Title Role</strong>, and <strong>Title Area</strong>.
            This allows for more precise filtering and searching. For example, "Assistant Director of Technology" is parsed as Modifier: "Assistant", Role: "Director", Area: "Technology".
          </p>
        </div>

        <div className="section">
          <h4>Title Modifier</h4>
          <p className="section-desc">Prefixes that modify the seniority or status of a role.</p>
          <div className="values-grid">
            {TITLE_MODIFIERS.map((value) => (
              <span key={value} className="value-chip">{value}</span>
            ))}
          </div>
        </div>

        <div className="section">
          <h4>Title Role</h4>
          <p className="section-desc">The primary job function or position type.</p>
          <div className="values-grid">
            {TITLE_ROLES.map((value) => (
              <span key={value} className="value-chip">{value}</span>
            ))}
          </div>
        </div>

        <div className="section">
          <h4>Title Area</h4>
          <p className="section-desc">The department, subject, or functional area of responsibility.</p>
          <div className="values-grid">
            {TITLE_AREAS.map((value) => (
              <span key={value} className="value-chip">{value}</span>
            ))}
          </div>
        </div>
      </div>
    </ReactModal>
  );
}
